import React, { useRef, useEffect } from 'react';
import { BlockShape } from './ShapeDefinitions';

interface ShapeBlockProps {
  shape: BlockShape;
  selected: boolean;
  onClick: (shape: BlockShape) => void;
  onDragStart: (shape: BlockShape) => void;
  selectedElement?: string | null;
}

const ShapeBlock: React.FC<ShapeBlockProps> = ({ 
  shape, 
  selected, 
  onClick,
  onDragStart,
  selectedElement
}) => {
  const { id, name, color } = shape;
  
  // Create an invisible drag image to remove the default label during drag
  const emptyImgRef = useRef<HTMLImageElement | null>(null);
  
  useEffect(() => {
    // Create a 1x1 transparent image to use as drag image
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; // transparent GIF
    emptyImgRef.current = img;
  }, []);
  
  // Determine the color based on the selected element
  const getElementColor = () => {
    if (!selectedElement) return color || '#FF0000';
    
    switch (selectedElement) {
      case 'Tree':
        return '#4CAF50'; // Green
      case 'Bridge':
        return '#A5744A'; // Brown
      case 'Basket':
        return '#FDD835'; // Yellow
      case 'River':
        return '#64B5F6'; // Blue
      case 'Road':
        return '#9E9E9E'; // Grey
      case 'House':
        return '#F8BBD0'; // Pink
      default:
        return color || '#FF0000'; // Default red
    }
  };
  
  const blockColor = getElementColor();
  
  // Shape-specific SVG icons based on their id
  const renderShapeIcon = () => {
    switch (id) {
      case 'single-pixel':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24">
            <rect x="4" y="4" width="16" height="16" fill={blockColor} />
          </svg>
        );
      case 'i-tetromino':
        return (
          <svg width="40" height="16" viewBox="0 0 40 16">
            <rect x="1" y="3" width="38" height="10" fill={blockColor} />
          </svg>
        );
      case 'long-rect':
        return (
          <svg width="36" height="24" viewBox="0 0 36 24">
            <rect x="2" y="4" width="32" height="16" fill={blockColor} />
          </svg>
        );
      case 'l-tetromino':
        return (
          <svg width="30" height="30" viewBox="0 0 30 30">
            <path d="M5,5 L5,20 L20,20 L20,15 L10,15 L10,5 Z" fill={blockColor} />
          </svg>
        );
      case 't-tetromino':
        return (
          <svg width="30" height="30" viewBox="0 0 30 30">
            <path d="M5,5 L25,5 L25,10 L18,10 L18,20 L12,20 L12,10 L5,10 Z" fill={blockColor} />
          </svg>
        );
      case 'stair-shape':
        return (
          <svg width="30" height="30" viewBox="0 0 30 30">
            <path d="M5,5 L10,5 L10,10 L15,10 L15,15 L20,15 L20,20 L15,20 L15,15 L10,15 L10,10 L5,10 Z" fill={blockColor} />
          </svg>
        );
      default:
        // Default fallback to the original matrix visualization
        return blockPreview();
    }
  };
  
  // Calculate dimensions for matrix preview (used as fallback)
  const rows = shape.matrix.length;
  const cols = shape.matrix[0].length;
  
  // Determine a good size for preview blocks based on matrix dimensions
  const maxDimension = Math.max(rows, cols);
  const cellSize = Math.max(Math.min(10, 50 / maxDimension), 6); // Adjust size based on matrix dimensions
  
  // Create a grid of blocks for preview - no outlines (used as fallback)
  const blockPreview = () => {
    return (
      <div 
        className="tetris-preview-container"
        style={{
          display: 'grid',
          gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
          gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
          margin: 'auto',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: selected ? 'translate(-50%, -50%) scale(1.1)' : 'translate(-50%, -50%)',
          transition: 'transform 0.2s ease',
        }}
      >
        {shape.matrix.map((row, rowIndex) => 
          row.map((cell, colIndex) => 
            cell && (
              <div
                key={`${rowIndex}-${colIndex}`}
                style={{
                  backgroundColor: blockColor,
                  border: 'none',
                  width: `${cellSize}px`,
                  height: `${cellSize}px`,
                  gridRow: rowIndex + 1,
                  gridColumn: colIndex + 1,
                }}
              />
            )
          )
        )}
      </div>
    );
  };
  
  // Add element class for CSS element-specific styling
  const elementClass = selectedElement ? `element-${selectedElement}` : '';
  
  return (
    <div 
      className={`shape-block ${selected ? 'selected' : ''} ${elementClass}`}
      onClick={() => onClick(shape)}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        
        // Use the transparent image as drag ghost to hide the default label
        if (emptyImgRef.current) {
          e.dataTransfer.setDragImage(emptyImgRef.current, 0, 0);
        }
        
        onDragStart(shape);
      }}
    >
      <div className="shape-preview">
        {renderShapeIcon()}
      </div>
      <div className="shape-name">{name}</div>
    </div>
  );
};

export default ShapeBlock; 